<template>
  <div class="case-pulse">
    <table>
      <thead>
        <tr>
          <th />
          <th />
          <th>
            <md-checkbox
              class="md-primary"
              :value="!caseConf.initialConditions.disablePulseStrengthsMatching"
              aria-label="Match Left and Right"
              data-testid="match-left-right-checkbox"
              @change="(value) => togglePulseStrengthsMatching(value)"
            >
              Match Left and Right Side
            </md-checkbox>
          </th>
        </tr>
        <tr>
          <th />
          <th>
            <section-title>Left Side</section-title>
          </th>
          <th>
            <section-title>Right Side</section-title>
          </th>
        </tr>
      </thead>
      <tbody data-testid="pulse-sliders-container">
        <tr
          v-for="(locations, groupName) in pulseLocationGroups"
          :key="groupName"
          v-testid="`${groupName}-row`"
        >
          <td class="group-name">{{ groupName }}</td>
          <td data-testid="left-side-slider-container">
            <pulse-strength-indicator
              :value="pulsesByLocation[locations[0].key].strength"
            />
            <vue-slider
              class="slider"
              data-testid="slider"
              :min="0"
              :max="5"
              :interval="1"
              :height="10"
              :dot-size="10"
              :adsorb="true"
              :disabled="readonly"
              :value="pulsesByLocation[locations[0].key].strength"
              :dot-attrs="{ 'aria-label': 'Pulse strength' }"
              @change="
                (strength) => updatePulseStrength(locations[0].key, strength)
              "
            />
          </td>
          <td data-testid="right-side-slider-container">
            <pulse-strength-indicator
              :value="pulsesByLocation[locations[1].key].strength"
            />
            <vue-slider
              class="slider"
              data-testid="slider"
              :min="0"
              :max="5"
              :interval="1"
              :height="10"
              :dot-size="10"
              :adsorb="true"
              :value="pulsesByLocation[locations[1].key].strength"
              :dot-attrs="{ 'aria-label': 'Pulse strength' }"
              :disabled="
                readonly || !caseConf.initialConditions.disablePulseStrengthsMatching
              "
              @change="
                (strength) => updatePulseStrength(locations[1].key, strength)
              "
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import VueSlider from 'vue-slider-component';

import SectionTitle from '../../common/SectionTitle.vue';
import PulseStrengthIndicator from './PulseStrengthIndicator.vue';

export default {
  name: 'case-pulse',
  components: {
    VueSlider,
    SectionTitle,
    PulseStrengthIndicator,
  },
  props: {
    pulseLocations: {
      type: Array,
      required: true,
    },
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    pulseLocationGroups() {
      return groupBy(this.pulseLocations, 'group');
    },
    pulsesByLocation() {
      return keyBy(
        this.caseConf.initialConditions
          ? this.caseConf.initialConditions.pulses
          : [],
        'location'
      );
    },
  },
  methods: {
    updatePulseStrength(locationKey, strength) {
      this.$emit('update', {
        initialConditions: {
          pulses: this.caseConf.initialConditions.pulses.map((pulse) =>
            pulse.location === locationKey ? { ...pulse, strength } : pulse
          ),
          disablePulseBpSync: true,
        },
      });
      if (!this.caseConf.initialConditions.disablePulseStrengthsMatching) {
        this.syncPulseGroupsStrengths();
      }
    },
    syncPulseGroupsStrengths() {
      this.$emit('update', {
        initialConditions: {
          pulses: flatten(
            map(this.pulseLocationGroups, (group) =>
              group.map((pulse) => ({
                ...this.pulsesByLocation[pulse.key],
                // Set the strength of the whole group to the strength of the first pulse
                // in the group
                strength: this.pulsesByLocation[group[0].key].strength,
              }))
            )
          ),
        },
      });
    },
    togglePulseStrengthsMatching(match) {
      if (match) {
        this.syncPulseGroupsStrengths();
      }
      this.$emit('update', {
        initialConditions: {
          disablePulseStrengthsMatching: !match,
        },
      });
    },
  },
};
</script>

<style scoped>
table {
  width: 100%;
}

th >>> * {
  margin: auto;
  margin-right: auto !important;
  text-align: center;
}

td {
  padding: 20px;
  padding-right: 80px;
}

.pulse-strength-indicator {
  display: inline-flex !important;
  margin-right: 10px;
}
.slider {
  display: inline-block;
  min-width: 100px;
}
</style>